import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { capitalizeName } from '@eika-infrastruktur/format';
import { AttentionMessage } from '@eika/attention-message';
import { Button, ButtonTypes } from '@eika/button';
import { IconTypes } from '@eika/icon';
import { Input } from '@eika/input';

import { Fieldset } from '../../../../components/shared/Fieldset';
import { NavigationButtons } from '../../../../components/shared/NavigationButtons';
import useTranslation from '../../../../hooks/useTranslation';
import useFormContext from '../../form/useFormContext';
import { EFakturaError } from '../../form/validate';
import useValidateCustomer from '../../hooks/useValidateCustomer';
import routes from '../../routes';
import { AmountSelectors } from '../AmountSelectors';

import './AddUserToEfaktura.scss';

const AddUserToEfaktura: React.FC = () => {
    const navigate = useNavigate();
    const {
        givenEfakturas,
        form: { state, handleChange, curryChange },
    } = useFormContext();
    const translated = useTranslation();

    const [validationErrors, setValidationErrors] = useState<Partial<EFakturaError> | undefined>();

    const currentUserInGiven = useMemo(
        () => givenEfakturas.data?.find((x) => x.customerNumber.value === state.ssn),
        [state.ssn, givenEfakturas.data],
    );

    const { validate, isLoading } = useValidateCustomer(currentUserInGiven, setValidationErrors);

    const onChange = (stateKey: 'name' | 'ssn', payload: string | undefined, isBlur = false) => {
        if (isBlur) {
            const newValue =
                stateKey === 'ssn' ? payload?.trim().replace(/\s/g, '') : payload?.trim().replace(/\s{2,}/g, ' ');
            handleChange({ type: stateKey, payload: newValue });
            return;
        }
        handleChange({ type: stateKey, payload });
        setValidationErrors((prev) => ({ ...prev, [stateKey]: undefined }));
    };

    const nextUrl = useMemo(
        () => (state.amountSelected === 'all' ? routes.summary : routes['efaktura-list']),
        [state.amountSelected],
    );

    return (
        <section className="efaktura-sharing__add-user-wrapper">
            <Fieldset legend={translated.eFakturaSharing.whoDoYouWantToShareWith}>
                <Input
                    value={state.ssn ?? ''}
                    onChange={(e) => onChange('ssn', e.target.value)}
                    label={translated.eFakturaSharing.ssn}
                    placeholder={translated.eFakturaSharing.writeSSN}
                    error={validationErrors?.ssn}
                    onBlur={(e) => onChange('ssn', e.target.value, true)}
                    name="ssn"
                    inputMode="numeric"
                    pattern="\d*"
                    data-testid="ssn-input"
                />
                <Input
                    value={state.name ?? ''}
                    onChange={(e) => onChange('name', e.target.value)}
                    label={translated.eFakturaSharing.name}
                    placeholder={translated.eFakturaSharing.writeName}
                    error={validationErrors?.name}
                    onBlur={(e) => onChange('name', capitalizeName(e.target.value), true)}
                    name="name"
                    data-testid="name-input"
                />
                {!!currentUserInGiven && (
                    <AttentionMessage
                        icon={IconTypes.UTROPSTEGN_24}
                        type="attention"
                        text={translated.eFakturaSharing.userAlreadyInGiven}
                        clickableItem={
                            <Button
                                buttonType={ButtonTypes.STANDARD_POSITIVE}
                                onClick={() => navigate(`${routes.update}/${currentUserInGiven.id.value}`)}
                            >
                                {translated.eFakturaSharing.editSharing}
                            </Button>
                        }
                    />
                )}
            </Fieldset>
            <AmountSelectors
                value={state.amountSelected}
                onChange={curryChange('amountSelected')}
                resetSelectedArray={() => handleChange({ type: 'selected', payload: undefined })}
                validationError={validationErrors?.amountSelected}
                setValidationError={(value) =>
                    setValidationErrors((prev) => ({
                        ...prev,
                        amountSelected: value,
                    }))
                }
            />
            <NavigationButtons
                className="efaktura-sharing__navigation-buttons"
                centerOnSmallerWidth
                onClick={validate}
                nextUrl={nextUrl}
                cancelUrl={routes.cancel}
                showNextButton={!currentUserInGiven}
                mainButtonIsLoading={isLoading}
            />
        </section>
    );
};

export default AddUserToEfaktura;
